<template>
  <div :id="groupId">
    <div v-for="attr_field in relevantFields" v-bind:key="attr_field.id">
      <div
        v-if="
          attr_field.type == 'text' ||
          attr_field.type == 'email' ||
          attr_field.type == 'link' ||
          attr_field.type == 'number'
        "
      >
        <form-control-input
          :label="attr_field.name"
          :type="attr_field.type"
          :code="attr_field.code"
          :name="attributeId(attr_field)"
          :serverError="serverErrors[attributeId(attr_field)]"
          :required="requiredByUser(attr_field) || requiredIfResults[attr_field.code]"
          :isPublic="toBool(attr_field.public)"
          :hidden="hideInputFieldIfInitValueFound(attr_field)"
          :error="attr_field.error"
          :help="attr_field.help"
          :defaultvalue="getDefaultValue(attr_field)"
          :visibleIfResult="visibleIfResults[attr_field.code]"
          :parentFormId="parentFormId"
          :prefixId="elementPrefixId"
          :maxlength="attr_field.max_length"
          :reload="resetCounters[attributeId(attr_field)] || 0"
          v-on:validate="onValidatedFormField"
        />
      </div>
      <div v-else-if="attr_field.type == 'valuelist' || attr_field.type == 'select'">
        <form-control-select
          :label="attr_field.name"
          :name="attributeId(attr_field)"
          :serverError="serverErrors[attributeId(attr_field)]"
          :code="attr_field.code"
          :required="requiredByUser(attr_field) || requiredIfResults[attr_field.code]"
          :isPublic="toBool(attr_field.public)"
          :hidden="hideInputFieldIfInitValueFound(attr_field)"
          :error="attr_field.error"
          :help="attr_field.help"
          :defaultvalue="toArray(getDefaultValue(attr_field))"
          :multiselect="attr_field.multiselect == 1"
          :options="attr_field.values"
          :prefixId="elementPrefixId"
          :visibleIfResult="visibleIfResults[attr_field.code]"
          v-on:validate="onValidatedFormField"
          :parentFormId="parentFormId"
          :reload="resetCounters[attributeId(attr_field)] || 0"
          :allowClear="true"
          :dependantvalues="allAttributeValuesByCode"
        />
      </div>
      <div v-else-if="attr_field.type == 'checkbox'">
        <form-control-checkbox
          :label="attr_field.name"
          :name="attributeId(attr_field)"
          :code="attr_field.code"
          :required="requiredByUser(attr_field) || requiredIfResults[attr_field.code]"
          :isPublic="toBool(attr_field.public)"
          :hidden="hideInputFieldIfInitValueFound(attr_field)"
          :error="attr_field.error"
          :serverError="serverErrors[attributeId(attr_field)]"
          :help="attr_field.help"
          :prefixId="elementPrefixId"
          :defaultvalue="toBool(getDefaultValue(attr_field))"
          :visibleIfResult="visibleIfResults[attr_field.code]"
          v-on:validate="onValidatedFormField"
          :reload="resetCounters[attributeId(attr_field)] || 0"
          :parentFormId="parentFormId"
        />
      </div>
      <div v-else-if="attr_field.type == 'textarea'">
        <form-control-textarea
          :label="attr_field.name"
          :name="attributeId(attr_field)"
          :code="attr_field.code"
          :required="requiredByUser(attr_field) || requiredIfResults[attr_field.code]"
          :isPublic="toBool(attr_field.public)"
          :hidden="hideInputFieldIfInitValueFound(attr_field)"
          :error="attr_field.error"
          :defaultvalue="getDefaultValue(attr_field)"
          :serverError="serverErrors[attributeId(attr_field)]"
          :help="attr_field.help"
          :prefixId="elementPrefixId"
          :maxlength="attr_field.max_length"
          :visibleIfResult="visibleIfResults[attr_field.code]"
          v-on:validate="onValidatedFormField"
          :parentFormId="parentFormId"
          :reload="resetCounters[attributeId(attr_field)] || 0"
          :textLimitConfig="{ maxCount: attr_field.max_length, cLimitText: 'Maximale Zeichenanzahl erreicht' }"
        />
      </div>
      <div v-else>
        <i>Unknown type "{{ attr_field.type }}" for attribute "{{ attr_field.code }}"!</i>
      </div>
    </div>
  </div>
</template>
<script>
import FormControlInput from '@/components/general/FormControl/Input';
import FormControlSelect from '@/components/general/FormControl/Select';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox';
import FormControlTextarea from '@/components/general/FormControl/TextArea';
import useConverter from '@/composables/general/useConverter';
import { userStore } from '@/store/user-store';
import { ref, toRefs } from '@vue/reactivity';
import { computed } from '@vue/runtime-core';

export default {
  name: 'form-control-attribute',

  emits: ['validate'],
  components: {
    FormControlInput,
    FormControlSelect,
    FormControlCheckbox,
    FormControlTextarea,
  },

  props: {
    fields: {
      required: true,
      type: Array,
    },
    groupName: {
      required: false,
      type: String,
      default: '',
    },
    parentFormId: {
      required: false,
      type: String,
      default: '',
    },
    elementPrefixId: {
      required: false,
      type: String,
      default: 'wdw-bms-attr',
    },
    serverErrors: {
      required: false,
      type: Object,
      default() {
        return {};
      },
    },
    requiredIfResults: {
      required: false,
      type: Object,
      default() {
        return {};
      },
    },
    visibleIfResults: {
      required: false,
      type: Object,
      default() {
        return {};
      },
    },
    // Will override existing defaultvalues from the attribute itself if exist
    initialValues: {
      required: false,
      type: Object,
      default() {
        return {};
      },
    },

    // If initial value is found, hide the input field to avoid further change
    hideOnInitialValueFound: {
      required: false,
      type: Boolean,
      default: false,
    },

    resetCounters: {
      // resetCounters/reload on every field
      required: false,
      type: Object,
      default() {
        return {};
      },
    },
  },

  setup(props, context) {
    const { groupName, elementPrefixId, initialValues, hideOnInitialValueFound } = toRefs(props);
    const allAttributeValuesByCode = ref({});

    //+---------------------------+
    //|         computed1         |
    //+---------------------------+
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });
    const isAdmin = computed(() => {
      return userStore.isAdmin();
    });

    //+--------------------------+
    //|     other methods        |
    //+--------------------------+
    const attributeId = attr => {
      return 'attribute-' + attr.id;
    };
    const { toBool, toArray } = useConverter();
    const getDefaultValue = attr => {
      const attrid = attributeId(attr);
      const overrideInit = initialValues.value;
      // 1. check overridden value first
      let defValue = overrideInit[attrid];

      // 2. check default value set by attribute
      if (!defValue) {
        defValue = attr.default_value;
      }

      // 3. check if it can be filled with user info
      if (!defValue && user) {
        const relData = attr.relation_data;
        if (relData) {
          if (relData.value_from_user_field == 'firstname') {
            defValue = user.value.firstname;
          } else if (relData.value_from_user_field == 'lastname') {
            defValue = user.value.lastname;
          } else if (relData.value_from_user_field == 'email') {
            defValue = user.value.email;
          } else if (relData.value_from_user_field == 'displayname') {
            defValue = user.value.public_name;
          }
        }
      }

      return defValue;
    };

    // Per T17350, whether or not to hide input field if initial value is found
    const hideInputFieldIfInitValueFound = attr => {
      const attrid = attributeId(attr);
      const overrideVal = initialValues.value[attrid];
      return overrideVal && overrideVal != null && overrideVal !== '' && hideOnInitialValueFound.value;
    };

    function relevantToUser(attr) {
      switch (attr.visible_to) {
        case 'none':
          return false;
        case 'system':
          return false;
        case 'user':
          return !isAdmin.value;
        case 'moderator':
          return isAdmin.value;
        default:
          return true;
      }
    }
    function requiredByUser(attr) {
      if (isAdmin.value) {
        return toBool(attr.required_admin);
      } else {
        return toBool(attr.required);
      }
    }

    //+---------------------------+
    //|         computed2         |
    //+---------------------------+
    const groupId = computed(() => {
      return elementPrefixId.value + '-attributegroup-' + groupName.value;
    });
    const relevantFields = computed(() => {
      return props.fields.filter(relevantToUser).sort(function (a, b) {
        return a.ordering - b.ordering;
      });
    });

    const onValidatedFormField = fieldValue => {
      // T22832: Save all validated attribute values
      allAttributeValuesByCode.value[fieldValue.code] = fieldValue.value;
      // Pass validation to the parent
      // NOTE: you can also pre-validate subgroup of attributes here before passing back to parent
      context.emit('validate', fieldValue);
    };

    return {
      // vars
      allAttributeValuesByCode,
      // Computed
      groupId,
      user,
      isLoggedIn,
      isAdmin,
      relevantFields,
      // other methods
      attributeId,
      toBool,
      toArray,
      getDefaultValue,
      requiredByUser,
      hideInputFieldIfInitValueFound,
      // Listeners
      onValidatedFormField,
    };
  },
};
</script>
